import { createContext, useContext, useState, useEffect } from "react";
import { initializeB24Frame } from "@bitrix24/b24jssdk";
import { message } from "antd";
import certificateStore from "../store/CertificateStore";
import employeeStore from "../store/EmployeeStore";
import equipmentStore from "../store/EquipmentStore";
import objectStore from "../store/ObjectStore";
import PermissionStore from "../store/PermissionStore";
import tagStore from "../store/TagStore";

const BitrixContext = createContext(null);

export const useBitrix = () => useContext(BitrixContext);

// Global reference to b24 frame for use outside React (e.g. openBitrixLink)
let _b24Instance = null;
export const getB24 = () => _b24Instance;

export const BitrixProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let intervalId;

    const init = async () => {
      try {
        // Dev mode bypass - use mock user
        if (process.env.NODE_ENV === 'development') {
          const mockUser = {
            ID: '1',
            NAME: 'Dev',
            LAST_NAME: 'User',
            EMAIL: 'dev@example.com',
            WORK_POSITION: 'Developer'
          };
          const mockToken = 'dev-mock-token-' + Date.now();

          setUser(mockUser);
          setAuth(mockToken);

          // Save to stores
          const stores = [
            certificateStore,
            employeeStore,
            equipmentStore,
            objectStore,
            PermissionStore,
            tagStore,
          ];

          stores.forEach(store => {
            store.setUser(mockUser.ID);
            store.setAuth(mockToken);
          });

          setLoading(false);
          console.log('🔧 Dev mode: Using mock Bitrix user');
          return;
        }

        // Production mode - real Bitrix24 integration
        const b24 = await initializeB24Frame();
        _b24Instance = b24;

        if (!b24 || !b24.callMethod) {
          throw new Error("BX24 SDK не инициализирован");
        }

        // Get current user
        const result = await b24.callMethod("user.current");
        const currentUser = result._data?.result;
        if (!currentUser) throw new Error("Не удалось получить пользователя");

        // Get token
        const authData = b24.auth.getAuthData()?.access_token;
        if (!authData) throw new Error("Не удалось получить токен");

        setUser(currentUser);
        setAuth(authData);

        // Save to stores
        const stores = [
          certificateStore,
          employeeStore,
          equipmentStore,
          objectStore,
          PermissionStore,
          tagStore,
        ];

        stores.forEach(store => {
          store.setUser(currentUser.ID);
          store.setAuth(authData);
        });

        // Interval to refresh token every 4 minutes
        intervalId = setInterval(() => {
          const newToken = b24.auth.getAuthData()?.access_token;
          if (newToken && newToken !== auth) {
            setAuth(newToken);
            stores.forEach(store => store.setAuth(newToken));
          }
        }, 1000 * 60 * 4);

      } catch (err) {
        console.error("Ошибка инициализации Bitrix:", err);
        setError(err.message || "Неизвестная ошибка");
        message.error(err.message || "Ошибка Bitrix");
      } finally {
        setLoading(false);
      }
    };

    init();

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  return (
    <BitrixContext.Provider value={{ user, auth, loading, error }}>
      {children}
    </BitrixContext.Provider>
  );
};
